import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { Store } from 'redux';
import { useSelector } from 'react-redux';
import { LogicMiddleware } from 'redux-logic';

import { list as listJobTypes } from 'modules/job-types/duck';
import { getProJobCategoriesForSignUp } from 'modules/marketing-pages/duck';
import { geoLocationSelector } from 'modules/locations/duck';
import Layout from 'modules/layout';
import { ProContent } from 'modules/static-pages/components/ProContent';
import { wrapper } from 'store/index';
import { RootState } from 'store/types';
import { setupPageAndLoadTranslations } from 'utils/page';

// TODO: Move to i18n
const seoData: Record<string, Record<string, any>> = {
  en: {
    seo: {
      description:
        'Whether you are a company or an individual, join our growing community and get paid for your expertise. Start for free. No commission. Keep your clients.',
      keywords: 'Zehitomo, Pro',
      ogDescription:
        'Whether you are a company or an individual, join our growing community and get paid for your expertise. Start for free. No commission. Keep your clients.',
      ogImage:
        'https://res.cloudinary.com/zehitomo/image/upload/v1652747398/assets/openGraphLogo.png',
      ogTitle: 'Pro Page',
    },
    title: 'Pro Page',
  },
  ja: {
    seo: {
      description:
        '集客サイト《ゼヒトモ》は、全国の工務店、自営業者、リフォーム大工、出張ネイリスト、各種オンラインレッスンの先生やパーソナルトレーナなどの全国のプロの集客・新規顧客獲得をサポートします。「今すぐ仕事を頼みたい」お客様からのご依頼とプロのお仕事をAIでマッチング、費用対効果の高いマーケティングを実現できます。成果報酬型の中でも、低コストの応募課金システムで、報酬は全額受け取り可能。広告費として、お客様に連絡を取る最初の１回だけ費用が発生する仕組みです。今すぐ登録して新規顧客を開拓しましょう。',
      keywords: 'ゼヒトモ, プロとして利用する',
      ogDescription:
        '集客サイト《ゼヒトモ》は、全国の工務店、自営業者、リフォーム大工、出張ネイリスト、各種オンラインレッスンの先生やパーソナルトレーナなどの全国のプロの集客・新規顧客獲得をサポートします。「今すぐ仕事を頼みたい」お客様からのご依頼とプロのお仕事をAIでマッチング、費用対効果の高いマーケティングを実現できます。成果報酬型の中でも、低コストの応募課金システムで、報酬は全額受け取り可能。広告費として、お客様に連絡を取る最初の１回だけ費用が発生する仕組みです。今すぐ登録して新規顧客を開拓しましょう。',
      ogImage:
        'https://res.cloudinary.com/zehitomo/image/upload/v1652747398/assets/openGraphLogo.png',
      ogTitle: '案件を受注 | 事業者・専門家として登録・出店 | ゼヒトモ',
    },
    title: '案件を受注 | 事業者・専門家として登録・出店 | ゼヒトモ',
  },
};

interface Props {
  content: any;
}

const ProPage: NextPage<Props> = ({ content }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const {
    query: {
      ip: ipAddress = '',
      jobType = language === 'en' ? '' : '依頼',
      location = language === 'en' ? '' : '近く',
    },
  } = useRouter();
  const { cityKey, prefectureKey } = useSelector(geoLocationSelector) as {
    cityKey: string;
    prefectureKey: string;
  };

  return (
    <Layout {...seoData[language]} language={language}>
      <ProContent
        cityKey={cityKey}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        content={content}
        ip={ipAddress}
        language={language}
        prefectureKey={prefectureKey}
        proLocation={location}
        proJobType={jobType}
      />
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async ({ defaultLocale, locale }) => {
    const { dispatch, logicMiddleware } = store as Store<RootState> & {
      logicMiddleware: LogicMiddleware;
    };

    const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
      defaultLocale,
      locale,
      namespaces: ['common', 'form', 'staticPages'],
      // @ts-expect-error Extended store
      store,
    });

    dispatch(getProJobCategoriesForSignUp());
    dispatch(listJobTypes({ proSignUp: true }));
    await logicMiddleware.whenComplete();

    return {
      props: {
        ...i18nData,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        content:
          currentLocale === 'en'
            ? require('modules/static-pages/contents/pro.en.json')
            : require('modules/static-pages/contents/pro.ja.json'),
      },
    };
  }
);

export default ProPage;
